import { components as c } from "@/components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useDiaryByIdQuery } from "@/features/diaries";
import { extractEventById } from "@/features/diaries/services/extractEventById";
import { services as s } from "@/services";
import { Button } from "@patient-watch/ui/button";
import { CheckCircle, ClipboardList, ArrowRight } from "lucide-react";

const EventCompletePage = () => {
    const { diaryId, eventId } = Route.useParams();
    const { data: diary } = useDiaryByIdQuery(diaryId);
    const event = extractEventById(diary, eventId);
    const navigate = useNavigate();

    // Get event details for display
    const getEventDescription = () => {
        if (event?.injections_view?.length) {
            const injection = event.injections_view[0];
            return injection.injection_name || "Injection";
        }
        if (event?.surgeries_view?.length) {
            const surgery = event.surgeries_view[0];
            return surgery.surgery_name || "Surgery";
        }
        if (event?.shockwaves_view?.length) {
            return "Shockwave Therapy";
        }
        if (event?.educations_view?.length) {
            const education = event.educations_view[0];
            return education.education_name || "Consultation";
        }
        if (event?.products_view?.length) {
            return event.products_view[0]?.product_name || "Product";
        }
        return "Treatment";
    };

    const eventDate = event?.shared_attributes?.reference_date
        ? s.time.formatDate(event.shared_attributes.reference_date)
        : "today";

    const sharedAttributeId = event?.shared_attributes?.shared_attribute_id;

    // Generate a new tracker ID for potential tracker creation
    const newTrackerId = crypto.randomUUID();

    const handleAddQuestionnaire = () => {
        navigate({
            to: "/templates/diary/$diaryId/trackers/$trackerId",
            params: { diaryId, trackerId: newTrackerId },
            search: { shared_attribute_id: sharedAttributeId },
        });
    };

    const handleFinish = () => {
        navigate({ to: `/patients/${diaryId}` });
    };

    return (
        <div className="space-y-6">
            <c.ui.Card>
                <c.ui.CardContent className="pt-6">
                    <div className="flex flex-col items-center space-y-4 text-center">
                        <div className="bg-green-100 p-3 rounded-full">
                            <CheckCircle className="w-8 h-8 text-green-600" />
                        </div>
                        <div className="space-y-2">
                            <h2 className="font-semibold text-xl">Treatment Added</h2>
                            <p className="text-muted-foreground">
                                {getEventDescription()} on {eventDate} has been recorded.
                            </p>
                        </div>
                    </div>
                </c.ui.CardContent>
            </c.ui.Card>

            <div className="space-y-3">
                <p className="text-muted-foreground text-sm">Would you like to track outcomes for this treatment?</p>
                <div className="flex flex-col gap-2">
                    <Button size="lg" className="w-full" onClick={handleAddQuestionnaire}>
                        <ClipboardList className="mr-2 w-5 h-5" />
                        Yes, add a questionnaire
                    </Button>
                    <Button variant="secondary" size="lg" className="w-full" onClick={handleFinish}>
                        No, I'm done
                        <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const Route = createFileRoute("/_app/templates/diary/$diaryId/events/$eventId/complete")({
    component: EventCompletePage,
});
